import React, { Fragment, useMemo, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Check, ChevronDown } from 'lucide-react';
// Same look as the Command Center prototype; extras live in todos-mockup.css.
import '../command-center/command-center.css';
import './todos-mockup.css';

// Visual mockup only: fictional data, local state, nothing is saved or sent.

type Group = 'overdue' | 'today' | 'upcoming';
type Priority = 'high' | 'medium' | 'low';

interface Subtask {
  id: string;
  label: string;
  done: boolean;
}

interface Todo {
  id: string;
  title: string;
  project: string;
  assignee: string;
  due: string;
  /** Days from today, used for sorting. */
  dueRank: number;
  group: Group;
  priority: Priority;
  note: string;
  subtasks: Subtask[];
  done: boolean;
}

const sub = (items: [string, boolean][]): Subtask[] =>
  items.map(([label, done], i) => ({ id: `s${i}`, label, done }));

const seed: Todo[] = [
  { id: 't1', title: 'Upload electrical rough-in photos', project: 'Alder Street Clinic', assignee: 'Jordan Lee', due: '7 days overdue', dueRank: -7, group: 'overdue', priority: 'high', note: 'The city needs photos of every panel and junction box before the rough-in inspection can be booked.', subtasks: sub([['Panel and sub-panel', true], ['Level 1 junction boxes', false], ['Level 2 junction boxes', false]]), done: false },
  { id: 't2', title: 'Complete fire-stopping checklist', project: 'Alder Street Clinic', assignee: 'Jordan Lee', due: '6 days overdue', dueRank: -6, group: 'overdue', priority: 'high', note: 'Required for the same inspection. Sign off each penetration.', subtasks: [], done: false },
  { id: 't3', title: 'Approve PO-1042 for framing lumber', project: 'Maple Ridge Residence', assignee: 'Marcus Hale', due: 'Today', dueRank: 0, group: 'today', priority: 'high', note: 'Rainier Lumber holds the quote until Friday. Framing starts Thursday.', subtasks: sub([['Compare with accepted bid', true], ['Approve and send', false]]), done: false },
  { id: 't4', title: 'Reply to Elena about kitchen tile', project: 'Maple Ridge Residence', assignee: 'Marcus Hale', due: 'Today', dueRank: 0, group: 'today', priority: 'medium', note: 'Ash zellige in 4×4. She travels Friday, so we need her answer by Thursday.', subtasks: [], done: false },
  { id: 't5', title: 'Walk the site with the electrician', project: 'Pine Hollow Addition', assignee: 'Priya Nair', due: 'Today', dueRank: 0, group: 'today', priority: 'low', note: 'Review the service panel upgrade and mark outlet locations.', subtasks: [], done: false },
  { id: 't6', title: 'Confirm concrete pour with Hollis Concrete', project: 'Dockside Lofts', assignee: 'Priya Nair', due: 'Tomorrow', dueRank: 1, group: 'upcoming', priority: 'medium', note: 'Weather may push the level 2 pour to Monday. Confirm pump and truck availability.', subtasks: sub([['Check Monday availability', false], ['Release Thursday slot', false]]), done: false },
  { id: 't7', title: 'Collect roofing bids and build comparison', project: 'Pine Hollow Addition', assignee: 'Marcus Hale', due: 'Wed, Oct 14', dueRank: 1, group: 'upcoming', priority: 'medium', note: 'Two of three bids are in. Owner meeting is Thursday.', subtasks: sub([['Summit Roofing', true], ['Alder & Sons', true], ['Keystone Roofing', false]]), done: false },
  { id: 't8', title: 'Send updated schedule to Delgado Drywall', project: 'Dockside Lofts', assignee: 'Marcus Hale', due: 'Thu, Oct 15', dueRank: 2, group: 'upcoming', priority: 'high', note: 'Start moves to Nov 4 after the window delay.', subtasks: [], done: false },
  { id: 't9', title: 'Order trim paint samples', project: 'Maple Ridge Residence', assignee: 'Dana Ortiz', due: 'Mon, Oct 19', dueRank: 6, group: 'upcoming', priority: 'low', note: '', subtasks: [], done: false },
  { id: 't10', title: 'Renew insurance certificate for Summit HVAC', project: 'Alder Street Clinic', assignee: 'Dana Ortiz', due: 'Fri, Oct 23', dueRank: 10, group: 'upcoming', priority: 'low', note: 'Current certificate ends Oct 24.', subtasks: [], done: false },
  { id: 't11', title: 'Order bathroom fixtures', project: 'Maple Ridge Residence', assignee: 'Priya Nair', due: 'Done Mon', dueRank: -2, group: 'today', priority: 'low', note: 'Ordered from Ferguson, arriving in two weeks.', subtasks: [], done: true },
  { id: 't12', title: 'Post daily log for Dockside Lofts', project: 'Dockside Lofts', assignee: 'Priya Nair', due: 'Done yesterday', dueRank: -1, group: 'today', priority: 'medium', note: '', subtasks: [], done: true },
];

type Filter = 'all' | 'due' | 'upcoming' | 'done';
type SortKey = 'title' | 'project' | 'assignee' | 'priority' | 'due';

const FILTERS: { id: Filter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'due', label: 'Due' },
  { id: 'upcoming', label: 'Upcoming' },
  { id: 'done', label: 'Done' },
];

const COLUMNS: { key: SortKey; label: string }[] = [
  { key: 'title', label: 'To-do' },
  { key: 'project', label: 'Project' },
  { key: 'assignee', label: 'Assigned to' },
  { key: 'priority', label: 'Priority' },
  { key: 'due', label: 'Due' },
];

const PRIORITY_LABEL: Record<Priority, string> = { high: 'High', medium: 'Medium', low: 'Low' };
const PRIORITY_RANK: Record<Priority, number> = { high: 0, medium: 1, low: 2 };

function matches(todo: Todo, filter: Filter): boolean {
  if (filter === 'done') return todo.done;
  if (todo.done) return false;
  if (filter === 'due') return todo.group !== 'upcoming';
  if (filter === 'upcoming') return todo.group === 'upcoming';
  return true;
}

function compare(a: Todo, b: Todo, key: SortKey): number {
  switch (key) {
    case 'priority':
      return PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority];
    case 'due':
      return a.dueRank - b.dueRank;
    default:
      return a[key].localeCompare(b[key]);
  }
}

export function TodosMockupPage() {
  const reduceMotion = useReducedMotion();
  const [todos, setTodos] = useState<Todo[]>(seed);
  const [filter, setFilter] = useState<Filter>('all');
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 } | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');

  const visible = useMemo(() => {
    const rows = todos.filter((t) => matches(t, filter));
    return sort ? [...rows].sort((a, b) => compare(a, b, sort.key) * sort.dir) : rows;
  }, [todos, filter, sort]);

  const openCount = todos.filter((t) => !t.done).length;
  const dueCount = todos.filter((t) => !t.done && t.group !== 'upcoming').length;
  const countFor = (f: Filter) => todos.filter((t) => matches(t, f)).length;

  const patch = (id: string, change: Partial<Todo>) =>
    setTodos((prev) => prev.map((t) => (t.id === id ? { ...t, ...change } : t)));

  const toggle = (todo: Todo) =>
    patch(todo.id, todo.done
      ? { done: false, due: 'Today', dueRank: 0, group: 'today' }
      : { done: true, due: 'Done just now', dueRank: -1 });

  const toggleSub = (todo: Todo, subId: string) =>
    patch(todo.id, { subtasks: todo.subtasks.map((s) => (s.id === subId ? { ...s, done: !s.done } : s)) });

  const cycleSort = (key: SortKey) =>
    setSort((cur) => (!cur || cur.key !== key ? { key, dir: 1 } : cur.dir === 1 ? { key, dir: -1 } : null));

  const addTodo = (e: React.FormEvent) => {
    e.preventDefault();
    const title = draft.trim();
    if (!title) return;
    const id = `n${Date.now()}`;
    setTodos((prev) => [
      { id, title, project: 'Unassigned', assignee: 'Marcus Hale', due: 'Today', dueRank: 0, group: 'today', priority: 'medium', note: '', subtasks: [], done: false },
      ...prev,
    ]);
    setFilter('all');
    setSort(null);
    setDraft('');
  };

  const t = reduceMotion ? { duration: 0 } : { duration: 0.28, ease: [0.22, 1, 0.36, 1] as const };

  return (
    <div className="bcc btd">
      <div className="bcc-wrap">
        <section className="bcc-hero">
          <p className="bcc-bob-status">
            <span className="bcc-pulse" aria-hidden="true" />
            Mockup, all data is fictional
          </p>
          <h2 className="bcc-hello">Your to-dos</h2>
          <p className="bcc-sub">
            {openCount} open, {dueCount} {dueCount === 1 ? 'needs' : 'need'} you today.
          </p>

          <div className="btd-bar">
            <div className="bcc-filters" role="tablist" aria-label="Filter to-dos">
              {FILTERS.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  role="tab"
                  aria-selected={filter === f.id}
                  className={`bcc-filter${filter === f.id ? ' is-on' : ''}`}
                  onClick={() => setFilter(f.id)}
                >
                  {f.label}
                  <span className="bcc-filter-count">{countFor(f.id)}</span>
                </button>
              ))}
            </div>
            <form className="btd-add" onSubmit={addTodo}>
              <input
                className="btd-add-field"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Add a to-do"
                aria-label="New to-do"
              />
              <button type="submit" className="bcc-act bcc-act-primary btd-add-btn" disabled={!draft.trim()}>
                Add
              </button>
            </form>
          </div>
        </section>

        <section className="btd-panel" aria-label="To-dos">
          <table className="btd-table">
            <thead>
              <tr>
                <th className="btd-col-check"><span className="btd-sr">Complete</span></th>
                {COLUMNS.map((c) => {
                  const active = sort?.key === c.key;
                  return (
                    <th
                      key={c.key}
                      className={`btd-col-${c.key}`}
                      aria-sort={active ? (sort!.dir === 1 ? 'ascending' : 'descending') : 'none'}
                    >
                      <button type="button" className="btd-sort" onClick={() => cycleSort(c.key)}>
                        {c.label}
                        <ChevronDown
                          className={`btd-sort-icon${active ? ' is-on' : ''}${active && sort!.dir === -1 ? ' is-flip' : ''}`}
                        />
                      </button>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {visible.map((todo) => {
                const isOpen = openId === todo.id;
                const doneSubs = todo.subtasks.filter((s) => s.done).length;
                return (
                  <Fragment key={todo.id}>
                    <tr className={`btd-tr${isOpen ? ' is-open' : ''}${todo.done ? ' is-done' : ''}`}>
                      <td className="btd-col-check">
                        <button
                          type="button"
                          className={`btd-check${todo.done ? ' is-on' : ''}`}
                          role="checkbox"
                          aria-checked={todo.done}
                          aria-label={`Mark "${todo.title}" ${todo.done ? 'open' : 'complete'}`}
                          onClick={() => toggle(todo)}
                        >
                          <Check className="btd-check-icon" />
                        </button>
                      </td>
                      <td className="btd-col-title" data-label="To-do">
                        <button
                          type="button"
                          className="btd-title"
                          aria-expanded={isOpen}
                          onClick={() => setOpenId(isOpen ? null : todo.id)}
                        >
                          {todo.title}
                          {todo.subtasks.length > 0 && (
                            <span className="btd-subcount">{doneSubs}/{todo.subtasks.length}</span>
                          )}
                        </button>
                      </td>
                      <td className="btd-col-project" data-label="Project">{todo.project}</td>
                      <td className="btd-col-assignee" data-label="Assigned to">{todo.assignee}</td>
                      <td className="btd-col-priority" data-label="Priority">
                        <span className={`btd-prio-pill btd-prio-${todo.priority}`}>
                          <span className="btd-prio-dot" aria-hidden="true" />
                          {PRIORITY_LABEL[todo.priority]}
                        </span>
                      </td>
                      <td className={`btd-col-due btd-due-${todo.done ? 'done' : todo.group}`} data-label="Due">{todo.due}</td>
                    </tr>
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <tr className="btd-detail-tr">
                          <td colSpan={6}>
                            <motion.div
                              className="btd-detail"
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={t}
                            >
                              <div className="btd-detail-in">
                                <div className="btd-detail-notes">
                                  <p className="bcc-block-label">Notes</p>
                                  <p className="bcc-block-text">{todo.note || 'No notes yet.'}</p>
                                  <div className="bcc-actions">
                                    <button type="button" className="bcc-act bcc-act-primary" onClick={() => toggle(todo)}>
                                      {todo.done ? 'Reopen' : 'Mark as complete'}
                                    </button>
                                    {!todo.done && (
                                      <button
                                        type="button"
                                        className="bcc-act bcc-act-soft"
                                        onClick={() => patch(todo.id, { due: 'Tomorrow', dueRank: 1, group: 'upcoming' })}
                                      >
                                        Snooze a day
                                      </button>
                                    )}
                                    <button type="button" className="bcc-act bcc-act-quiet">Reassign</button>
                                  </div>
                                </div>
                                {todo.subtasks.length > 0 && (
                                  <div className="btd-subs">
                                    <p className="bcc-block-label">
                                      Checklist<span className="btd-sub-count">{doneSubs} of {todo.subtasks.length}</span>
                                    </p>
                                    <ul className="btd-sublist">
                                      {todo.subtasks.map((s) => (
                                        <li key={s.id}>
                                          <button
                                            type="button"
                                            className="btd-sub"
                                            role="checkbox"
                                            aria-checked={s.done}
                                            onClick={() => toggleSub(todo, s.id)}
                                          >
                                            <span className={`btd-check btd-check-sm${s.done ? ' is-on' : ''}`} aria-hidden="true">
                                              <Check className="btd-check-icon" />
                                            </span>
                                            <span className={s.done ? 'btd-struck' : ''}>{s.label}</span>
                                          </button>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            </motion.div>
                          </td>
                        </tr>
                      )}
                    </AnimatePresence>
                  </Fragment>
                );
              })}
            </tbody>
          </table>

          {visible.length === 0 && (
            <div className="bcc-empty">
              <p className="bcc-empty-title">Nothing here.</p>
              <p className="bcc-empty-text">Add a to-do above or switch filters.</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export default TodosMockupPage;
